/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  McpServerToolsetSummary,
  McpServerToolsetSummary$inboundSchema,
} from "./mcpservertoolsetsummary.js";

/**
 * A first-party backend Speakeasy serves itself, if any. approvals is the server agents call request_approval on; Speakeasy creates it with a project's first approval policy and manages its backend.
 */
export const BuiltinBackend = {
  Approvals: "approvals",
} as const;
/**
 * A first-party backend Speakeasy serves itself, if any. approvals is the server agents call request_approval on; Speakeasy creates it with a project's first approval policy and manages its backend.
 */
export type BuiltinBackend = OpenEnum<typeof BuiltinBackend>;

/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export const McpServerNetworkAccessMode = {
  PublicOnly: "public_only",
  Dual: "dual",
  PrivateOnly: "private_only",
} as const;
/**
 * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
 */
export type McpServerNetworkAccessMode = OpenEnum<
  typeof McpServerNetworkAccessMode
>;

/**
 * The visibility of an MCP server
 */
export const McpServerVisibility = {
  Disabled: "disabled",
  Private: "private",
  Public: "public",
} as const;
/**
 * The visibility of an MCP server
 */
export type McpServerVisibility = OpenEnum<typeof McpServerVisibility>;

/**
 * An MCP server configuration: authentication, environment, and backend selection for an MCP server.
 */
export type McpServer = {
  /**
   * A first-party backend Speakeasy serves itself, if any. approvals is the server agents call request_approval on; Speakeasy creates it with a project's first approval policy and manages its backend.
   */
  builtinBackend?: BuiltinBackend | undefined;
  /**
   * When the MCP server was created
   */
  createdAt: Date;
  /**
   * The ID of the environment associated with the server
   */
  environmentId?: string | undefined;
  /**
   * Whether any endpoint routes to this server, including one on a custom domain that is not live yet. Absent when it could not be read.
   */
  hasEndpoint?: boolean | undefined;
  /**
   * The ID of the MCP server
   */
  id: string;
  /**
   * A human-readable display name for the server
   */
  name?: string | undefined;
  /**
   * The network surfaces through which a Speakeasy-hosted MCP server may be reached.
   */
  networkAccessMode: McpServerNetworkAccessMode;
  /**
   * The slug of this server's primary platform endpoint, the oldest one. Absent when no platform endpoint exists; never a custom-domain slug.
   */
  platformEndpointSlug?: string | undefined;
  /**
   * The ID of the endpoint that stands for this server when a single address must be chosen: domain-root custom-domain endpoints first, then other custom-domain endpoints, then platform endpoints. Absent when the server has no addressable endpoint.
   */
  primaryEndpointId?: string | undefined;
  /**
   * The public MCP URL of the primary endpoint. Absent when the server has no addressable endpoint.
   */
  primaryEndpointUrl?: string | undefined;
  /**
   * The project ID this MCP server belongs to
   */
  projectId: string;
  /**
   * The ID of the remote MCP server used as the backend
   */
  remoteMcpServerId?: string | undefined;
  /**
   * A URL-safe, project-unique slug derived server-side from the name and ID
   */
  slug?: string | undefined;
  /**
   * The ID of the tool variations group enabling MCP tool filtering for this server, if any.
   */
  toolVariationsGroupId?: string | undefined;
  /**
   * A summary of the toolset backing a hosted MCP server.
   */
  toolset?: McpServerToolsetSummary | undefined;
  /**
   * The ID of the toolset used as the backend
   */
  toolsetId?: string | undefined;
  /**
   * The ID of the tunneled MCP server used as the backend
   */
  tunneledMcpServerId?: string | undefined;
  /**
   * The ID of the unproxied MCP server used as the backend, if any. A server backed by an unproxied MCP server is never proxied by Speakeasy.
   */
  unproxiedMcpServerId?: string | undefined;
  /**
   * When the MCP server was last updated
   */
  updatedAt: Date;
  /**
   * The ID of the user session issuer that gates OAuth-based MCP client authentication for this server, if any.
   */
  userSessionIssuerId?: string | undefined;
  /**
   * The visibility of an MCP server
   */
  visibility: McpServerVisibility;
};

/** @internal */
export const BuiltinBackend$inboundSchema: z.ZodMiniType<
  BuiltinBackend,
  unknown
> = openEnums.inboundSchema(BuiltinBackend);

/** @internal */
export const McpServerNetworkAccessMode$inboundSchema: z.ZodMiniType<
  McpServerNetworkAccessMode,
  unknown
> = openEnums.inboundSchema(McpServerNetworkAccessMode);

/** @internal */
export const McpServerVisibility$inboundSchema: z.ZodMiniType<
  McpServerVisibility,
  unknown
> = openEnums.inboundSchema(McpServerVisibility);

/** @internal */
export const McpServer$inboundSchema: z.ZodMiniType<McpServer, unknown> = z
  .pipe(
    z.object({
      builtin_backend: z.optional(BuiltinBackend$inboundSchema),
      created_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      environment_id: z.optional(z.string()),
      has_endpoint: z.optional(z.boolean()),
      id: z.string(),
      name: z.optional(z.string()),
      network_access_mode: McpServerNetworkAccessMode$inboundSchema,
      platform_endpoint_slug: z.optional(z.string()),
      primary_endpoint_id: z.optional(z.string()),
      primary_endpoint_url: z.optional(z.string()),
      project_id: z.string(),
      remote_mcp_server_id: z.optional(z.string()),
      slug: z.optional(z.string()),
      tool_variations_group_id: z.optional(z.string()),
      toolset: z.optional(McpServerToolsetSummary$inboundSchema),
      toolset_id: z.optional(z.string()),
      tunneled_mcp_server_id: z.optional(z.string()),
      unproxied_mcp_server_id: z.optional(z.string()),
      updated_at: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      user_session_issuer_id: z.optional(z.string()),
      visibility: McpServerVisibility$inboundSchema,
    }),
    z.transform((v) => {
      return remap$(v, {
        "builtin_backend": "builtinBackend",
        "created_at": "createdAt",
        "environment_id": "environmentId",
        "has_endpoint": "hasEndpoint",
        "network_access_mode": "networkAccessMode",
        "platform_endpoint_slug": "platformEndpointSlug",
        "primary_endpoint_id": "primaryEndpointId",
        "primary_endpoint_url": "primaryEndpointUrl",
        "project_id": "projectId",
        "remote_mcp_server_id": "remoteMcpServerId",
        "tool_variations_group_id": "toolVariationsGroupId",
        "toolset_id": "toolsetId",
        "tunneled_mcp_server_id": "tunneledMcpServerId",
        "unproxied_mcp_server_id": "unproxiedMcpServerId",
        "updated_at": "updatedAt",
        "user_session_issuer_id": "userSessionIssuerId",
      });
    }),
  );

export function mcpServerFromJSON(
  jsonString: string,
): SafeParseResult<McpServer, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => McpServer$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'McpServer' from JSON`,
  );
}
